import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setLoader } from "../../Store/Slice/techsbookSlice";
import {useLazyAddJobLineItemsQuery} from "../../Store/Builder/jobs.api";
import { useLazyGetTypeOfServicesQuery } from "../../Store/Builder/jobs.api";
import Popup from "../Popup";
import CountInput from "../../UI/CountInput";
import { CustomSelect } from "../../UI/Select";
import showToast from "../../Global/Hooks/toast";
import useGetJobs from "../../Global/Api/Jobs/getJobs.api";
import useFormatTime from "../../Global/Api/Shared/useFormatTime";
import useGetProductApi from "../../Global/Api/Schedule/getProductApi";

export default function AddLineItems({ setShowAddLine, currentJob }) {
    const { formatTime } = useFormatTime();
    const Jobs = useGetJobs({ allList: false });
    const dispatch = useDispatch();
    const productOptions = useGetProductApi(currentJob.id)?.options;
    const accountData = useSelector((state) => state.rootReducer.techsbookSlice.userData);
    const [addJobLineItems] = useLazyAddJobLineItemsQuery();
    const [getTypeOfServices] = useLazyGetTypeOfServicesQuery();

    const [localLineItems, setLocalLineItems] = useState({
        items: [{
            id: 1,
            product: '',
            type_of_service: '',
            type_of_services: [],
            quantity: 1,
            price: 0,
            approximate_time: null,
        }],
    });
    const [isButtonDisabled, setIsButtonDisabled] = useState(true);

    const closeAddLineItemsModal = () => setShowAddLine(false);

    const incrementLineItems = useCallback(() => {
        setLocalLineItems(prevState => ({
            items: [
                ...prevState.items,
                {
                    id: prevState.items.length + 1,
                    product: '',
                    type_of_service: '',
                    type_of_services: [],
                    quantity: 1,
                    price: 0,
                    approximate_time: null,
                },
            ],
        }));
    }, []);

    const handleSubmitLineItems = async (e) => {
        e.preventDefault();

        const body = new FormData();

        localLineItems?.items?.map((item, idx) => {
            body.append(`job_items[${idx}][product_service_id]`, item?.type_of_service)
            body.append(`job_items[${idx}][qty]`, item?.quantity)
        });

        if (!isButtonDisabled) {
            const id = currentJob.id
            try {
                dispatch(setLoader(true));
                await addJobLineItems({id, body}).unwrap();
                showToast('Line items successfully submitted!', 'success');
                Jobs.getById(currentJob?.title);
                setShowAddLine(false);
                dispatch(setLoader(false));
            } catch (error) {
                dispatch(setLoader(false));
                showToast(error?.data?.message, 'error');
            }
        }

        closeAddLineItemsModal()
    };

    const handleSelected = (field, id, value) => {
        setLocalLineItems(prevState => ({
            items: prevState.items.map(item => item.id === id
                ? {
                    ...item,
                    [field]: field === 'product' ? value : value?.value || value,
                    ...(field === 'product' && {
                        type_of_service: '',
                        approximate_time: null,
                        price: 0,
                        is_main: null,
                        quantity: 1,
                    }),
                }
                : item),
        }));
    };

    useEffect(() => {
        const allFieldsFilled = localLineItems.items.every(item => item.product && item.type_of_service && item.quantity > 0);
        setIsButtonDisabled(!allFieldsFilled);
    }, [localLineItems]);

    const handleProductSelect = (item, selected) => {
        const product_id = selected?.value;
        handleSelected("product", item?.id, selected);

        dispatch(setLoader(true));
        getTypeOfServices(product_id, currentJob.id)
            .unwrap()
            .then((res) => {
                dispatch(setLoader(false));
                const resData = res?.map((item) => ({
                    value: item?.id,
                    label: item?.name,
                    ...item,
                }));

                setLocalLineItems((prevState) => ({
                    items: prevState.items.map((i) =>
                        i.id === item.id
                            ? { ...i, type_of_services: resData }
                            : i
                    ),
                }));
            })
            .catch((err) => {
                dispatch(setLoader(false));
                showToast(err?.data?.message ?? "Error", "error");
            });
    };

    return (
        <Popup
            title="Add line item"
            closePopup={closeAddLineItemsModal}
            contentStyles={{ padding: '0' }}
            headStyles={{ padding: '28px 32px', border: 'none' }}
        >
            <div className="add-line-item-modal">
                <div className="add-line-item-modal_body">
                    <form className="line-items-form" onSubmit={(e) => e.preventDefault()}>
                        <div className="line-items-form_head line-items-form_row">
                            <p>Product</p>
                            <p>Image</p>
                            <p>Type of service</p>
                            <p>Quantity</p>
                            <p>Price</p>
                            <p>Approximate time</p>
                        </div>
                        {localLineItems?.items?.map((item, index) => (
                            <div className="line-items-form_row" key={index}>
                                <div>
                                    <CustomSelect
                                        selected={item?.product}
                                        options={productOptions}
                                        handleSelect={(selected) => handleProductSelect(item, selected)}
                                        placeholder={"Select product"}
                                        searchable={true}
                                    />
                                </div>
                                <div className="empty-img">
                                    {item?.product?.image ? (
                                        <img src={item?.product?.image} alt="product image" className='empty-img_img'/>
                                    ) : (
                                        <img src="/assets/icons/empty-img.svg" alt="#"/>
                                    )}
                                </div>
                                <div>
                                    <CustomSelect
                                        selected={item?.type_of_services.find(el => el.value === item.type_of_service)}
                                        options={item?.type_of_services}
                                        handleSelect={(selected) => handleSelected("type_of_service", item?.id, selected)}
                                        placeholder={"Select service"}
                                    />
                                </div>
                                <div>
                                    <CountInput
                                        disabled={!item?.type_of_service}
                                        initialCount={item?.quantity}
                                        minCount={1}
                                        handleChange={(count) => handleSelected("quantity", item?.id, count)}
                                    />
                                </div>
                                <div>
                                    {accountData?.country_currency_sign}{' '}
                                    {item?.type_of_services?.find(el => el.value === item?.type_of_service)?.price && item?.quantity
                                        ? (
                                            parseFloat(item.type_of_services.find(el => el.value === item.type_of_service)?.price) * item.quantity % 1 === 0
                                                ? parseFloat(item.type_of_services.find(el => el.value === item.type_of_service)?.price) * item.quantity
                                                : (parseFloat(item.type_of_services.find(el => el.value === item.type_of_service)?.price) * item.quantity).toFixed(2)
                                        )
                                        : 0}
                                </div>
                                <div className='line-items-form_row_last'>
                                    <span>
                                        {item?.type_of_services?.find(el => el.value === item?.type_of_service)?.time && item?.quantity
                                            ? `${formatTime(item.type_of_services.find(el => el.value === item.type_of_service)?.time * item.quantity)} hrs`
                                            : '00:00 hrs'}
                                    </span>
                                    {localLineItems.items.length > 1 && index !== 0 && (
                                        <img
                                            className="lineitems__remove"
                                            src="/assets/icons/close.svg"
                                            alt="close"
                                            onClick={() =>
                                                setLocalLineItems(prevState => ({
                                                    items: prevState.items.filter(lineItem => lineItem.id !== item.id),
                                                }))
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                        <div className="line-items-form_row">
                            <button className="jobLineItems__add-btn" onClick={incrementLineItems}>+ Add item</button>
                            <div></div>
                            <div className='total-text'>Total number of vehicles</div>
                            <div>
                                <CountInput
                                    disabled
                                    initialCount={localLineItems.items?.reduce((total, item) => total + item.quantity, 0)}
                                    minCount={1}
                                />
                            </div>
                            <div>
                                {accountData?.country_currency_sign}{' '}
                                {localLineItems.items?.reduce((total, item) => {
                                    const service = item.type_of_services.find(el => el.value === item.type_of_service);
                                    const price = service ? service.price * item.quantity : 0;
                                    return total + price;
                                }, 0)}
                            </div>
                            <div>
                                {(() => {
                                    const totalMinutes = localLineItems.items?.reduce((total, item) => {
                                        const service = item.type_of_services.find(el => el.value === item.type_of_service);
                                        const time = service ? service.time : 0;
                                        return total + (time * item.quantity);
                                    }, 0);

                                    const hours = Math.floor(totalMinutes / 60);
                                    const minutes = totalMinutes % 60;

                                    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} hrs`;
                                })()}
                            </div>
                        </div>
                    </form>
                    <div className="click-alert">
                        <img src="/assets/icons/alert-circle.svg" alt=""/>
                        <p>
                            After clicking the ‘Confirm’ button, the payment will be automatically charged to the card
                            you provided.
                        </p>
                    </div>
                </div>
                <div className="add-line-item-modal_footer">
                    <div className="add-line-item-modal_footer_left">
                    <img src="/assets/icons/wallet-add-line-item.svg" alt=""/>
                        <span>{currentJob?.payments[0]?.payment_method?.payment_method_data?.brand ?? 'visa'}: { currentJob?.payments[0]?.payment_method?.payment_method_data?.last4 }</span>
                    </div>
                    <button
                        type="submit"
                        disabled={isButtonDisabled}
                        onClick={handleSubmitLineItems}
                        className={`add-line-item-modal_footer_btn ${
                            isButtonDisabled ? 'btn-not-allowed' : 'btn-allowed'
                        }`}
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </Popup>
    );
}
